import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, CreditCard, Dumbbell, Target, TrendingUp, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import api from "../../lib/api";

function StatCard({ icon: Icon, label, value, sub, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-5">
        <div className="flex items-start gap-2 justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className={`text-xl font-black font-heading ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MemberDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const member = JSON.parse(localStorage.getItem("member_data") || "{}");

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const res = await api.get("/member-portal/dashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
      <AlertCircle className="h-8 w-8" />
      <p>{error}</p>
    </div>
  );

  const ms = data?.currentMembership;
  const statusColor = (data?.membershipStatus === "Active" || data?.membershipStatus === "Partial")
    ? "text-green-500"
    : data?.membershipStatus === "Expired" ? "text-red-500" : "text-muted-foreground";

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-2xl font-black font-heading uppercase tracking-wide">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Welcome back{member.name ? ` ${member.name}` : ""}! Here's your activity summary.</p>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Attendance This Month" value={data?.attendanceThisMonth ?? 0} sub="check-ins" />
        <StatCard
          icon={CreditCard}
          label="Membership"
          value={data?.currentMemberships && data.currentMemberships.length > 1 ? `${data.currentMemberships.length} Active Plans` : (ms ? ms.packageName : "None")}
          sub={data?.currentMemberships && data.currentMemberships.length > 1 ? "Multiple memberships active" : (ms ? `${ms.status} • Expires ${new Date(ms.endDate).toLocaleDateString("en-IN")}` : "No Active Membership")}
          color={statusColor}
        />
        <StatCard icon={Dumbbell} label="Active Workouts" value={data?.activeWorkouts ?? 0} sub="assigned plans" />
        <StatCard icon={Target} label="Active Goals" value={data?.activeGoals ?? 0} sub="in progress" />
      </div>

      {/* Current Membership progress */}
      {data?.currentMemberships && data.currentMemberships.length > 0 ? (
        data.currentMemberships.map((membershipItem, index) => (
          <Card key={index} className="bg-card border-border mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>
                  {data.currentMemberships.length > 1 ? `Current Membership (${index + 1})` : "Current Membership"}
                </span>
                {data.currentMemberships.length > 1 && (
                  <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded font-sans font-medium lowercase">
                    {index === 0 ? "Primary" : "Additional"}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{membershipItem.packageName}</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${["Active", "Partial"].includes(membershipItem.status) ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
                  }`}>{membershipItem.status}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{new Date(membershipItem.startDate).toLocaleDateString("en-IN")}</span>
                  <span>{membershipItem.daysRemaining} days left</span>
                  <span>{new Date(membershipItem.endDate).toLocaleDateString("en-IN")}</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(membershipItem.daysProgress, 100)}%` }}
                  />
                </div>
              </div>
              {membershipItem.balanceDue > 0 && (
                <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Balance due: ₹{membershipItem.balanceDue.toLocaleString("en-IN")}
                </div>
              )}
            </CardContent>
          </Card>
        ))
      ) : (
        ms && (
          <Card className="bg-card border-border mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">Current Membership</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{ms.packageName}</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${["Active", "Partial"].includes(ms.status) ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
                  }`}>{ms.status}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{new Date(ms.startDate).toLocaleDateString("en-IN")}</span>
                  <span>{ms.daysRemaining} days left</span>
                  <span>{new Date(ms.endDate).toLocaleDateString("en-IN")}</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(ms.daysProgress, 100)}%` }}
                  />
                </div>
              </div>
              {ms.balanceDue > 0 && (
                <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Balance due: ₹{ms.balanceDue.toLocaleString("en-IN")}
                </div>
              )}
            </CardContent>
          </Card>
        )
      )}

      {/* Recent Attendance */}
      {data?.recentAttendance?.length > 0 && (
        <Card className="bg-card border-border mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Recent Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recentAttendance.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm">{new Date(r.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{r.checkIn ? new Date(r.checkIn).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
